import { useState, useEffect } from 'react'
import axios from 'axios'
import { Rocket, RefreshCw, AlertTriangle, PauseCircle, PlayCircle, CheckCircle2 } from 'lucide-react'
import { RolloutDetailPanel } from './RolloutDetailPanel'

const API_BASE = import.meta.env.VITE_RAG_API_URL || 'http://localhost:8000'

interface Rollout {
    id: string
    policy_name: string
    status: 'RUNNING' | 'PAUSED_BY_RISK' | 'COMPLETED' | 'ROLLED_BACK'
    current_impact_rate: number
    target_impact_rate: number
}

export function RolloutManager() {
    const [rollouts, setRollouts] = useState<Rollout[]>([])
    const [isLoading, setIsLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [lastUpdated, setLastUpdated] = useState<Date | null>(null)
    const [selectedRolloutId, setSelectedRolloutId] = useState<string | null>(null)

    const fetchData = async () => {
        setIsLoading(true)
        setError(null)
        try {
            const { data } = await axios.get(`${API_BASE}/rollout/active`)
            setRollouts(data.active_rollouts || [])
            setLastUpdated(new Date())
        } catch (err: any) {
            console.warn('Rollout fetch failed:', err.message)
            setError('Falha na conexão com servidor')
        } finally {
            setIsLoading(false)
        }
    }

    useEffect(() => {
        fetchData()
    }, [])

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'RUNNING': return 'text-green-400 bg-green-500/10 border-green-500/20'
            case 'PAUSED_BY_RISK': return 'text-orange-400 bg-orange-500/10 border-orange-500/20'
            case 'ROLLED_BACK': return 'text-red-400 bg-red-500/10 border-red-500/20'
            default: return 'text-gray-400 bg-gray-500/10 border-gray-500/20'
        }
    }

    const getStatusIcon = (status: string) => {
        switch (status) {
            case 'RUNNING': return <PlayCircle className="w-4 h-4" />
            case 'PAUSED_BY_RISK': return <PauseCircle className="w-4 h-4" />
            case 'COMPLETED': return <CheckCircle2 className="w-4 h-4" />
            default: return <AlertTriangle className="w-4 h-4" />
        }
    }

    return (
        <div className="max-w-4xl mx-auto p-6">
            {/* Header */}
            <div className="flex justify-between items-center mb-8">
                <div>
                    <h2 className="text-2xl font-bold text-white flex items-center gap-3">
                        <Rocket className="w-6 h-6 text-purple-500" />
                        Rollout Manager
                    </h2>
                    <p className="text-gray-400 text-sm mt-1">
                        Gerencie e monitore a implantação progressiva de políticas (Canary/Blue-Green).
                    </p>
                </div>

                <div className="flex items-center gap-4">
                    <span className="text-xs text-gray-500 font-mono">
                        {lastUpdated ? lastUpdated.toLocaleTimeString() : '--'}
                    </span>
                    <button
                        onClick={fetchData}
                        disabled={isLoading}
                        className="p-2 bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg transition-colors text-gray-300"
                    >
                        <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
                    </button>
                </div>
            </div>

            {/* Content */}
            <div className="space-y-4">
                {error ? (
                    <div className="p-8 bg-red-900/10 border border-red-500/20 rounded-2xl flex flex-col items-center text-center">
                        <AlertTriangle className="w-12 h-12 text-red-500/50 mb-4" />
                        <h3 className="text-red-400 font-medium">Serviço Indisponível</h3>
                        <p className="text-red-300/70 text-sm mt-2 max-w-md">
                            Não foi possível conectar ao serviço de Rollout. Verifique se o backend está online.
                        </p>
                        <button
                            onClick={fetchData}
                            className="mt-6 px-4 py-2 bg-red-500/10 hover:bg-red-500/20 border border-red-500/30 rounded-lg text-red-300 text-sm transition-colors"
                        >
                            Tentar Novamente
                        </button>
                    </div>
                ) : rollouts.length > 0 ? (
                    rollouts.map((rollout) => (
                        <div
                            key={rollout.id}
                            onClick={() => setSelectedRolloutId(rollout.id)}
                            className="bg-white/5 border border-white/10 rounded-xl p-5 hover:bg-white/[0.07] transition-colors relative overflow-hidden group cursor-pointer"
                        >
                            <div className="absolute top-0 left-0 w-1 h-full bg-gradient-to-b from-purple-500 to-blue-500 opacity-50"></div>

                            <div className="flex justify-between items-start mb-4">
                                <div>
                                    <h3 className="text-lg font-semibold text-white font-mono">{rollout.policy_name}</h3>
                                    <div className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-xs font-medium mt-2 border ${getStatusColor(rollout.status)}`}>
                                        {getStatusIcon(rollout.status)}
                                        {rollout.status}
                                    </div>
                                </div>

                                <div className="text-right">
                                    <span className="text-xs text-gray-500 uppercase tracking-wider block mb-1">Impact Rate</span>
                                    <span className="text-2xl font-bold text-white font-mono">
                                        {(rollout.current_impact_rate * 100).toFixed(0)}%
                                    </span>
                                    <span className="text-sm text-gray-500 ml-1">
                                        / {(rollout.target_impact_rate * 100).toFixed(0)}%
                                    </span>
                                </div>
                            </div>

                            {/* Progress Bar */}
                            <div className="relative h-2 bg-black/40 rounded-full overflow-hidden">
                                <div
                                    className="absolute top-0 left-0 h-full bg-gradient-to-r from-purple-500 to-blue-500 transition-all duration-500 ease-out"
                                    style={{ width: `${(rollout.current_impact_rate / rollout.target_impact_rate) * 100}%` }}
                                ></div>
                            </div>

                            <div className="mt-2 flex justify-between text-[10px] text-gray-500 font-mono">
                                <span>0%</span>
                                <span>Target: {(rollout.target_impact_rate * 100).toFixed(0)}%</span>
                            </div>
                        </div>
                    ))
                ) : (
                    <div className="p-12 border-2 border-dashed border-white/5 rounded-2xl flex flex-col items-center justify-center text-center">
                        <div className="w-16 h-16 bg-white/5 rounded-full flex items-center justify-center mb-4">
                            <Rocket className="w-8 h-8 text-gray-600" />
                        </div>
                        <h3 className="text-gray-300 font-medium">Nenhum Rollout Ativo</h3>
                        <p className="text-gray-500 text-sm mt-2 max-w-sm">
                            Todas as políticas estão estáveis ou não há implantações em andamento no momento.
                        </p>
                    </div>
                )}
            </div>

            {/* Detail Panel */}
            {selectedRolloutId && (
                <RolloutDetailPanel
                    rolloutId={selectedRolloutId}
                    onClose={() => setSelectedRolloutId(null)}
                />
            )}
        </div>
    )
}
